import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UtilityService } from 'src/app/services/utility.service';
import { UtilityConfigResponse } from 'src/app/interfaces/responses/models';
import { ToastService } from 'src/app/services/toast.service';
import { stringifyBooleans, formatToApiDate, toBoolean } from 'src/app/shared/utils/form-utils';
import { BaseComponent } from 'src/app/shared/base.component';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-utility-config',
  templateUrl: './utility-config.component.html',
  styleUrls: ['./utility-config.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UtilityConfigComponent extends BaseComponent implements OnInit {

  utilityId = '';
  utilityConfig: UtilityConfigResponse | null = null;
  isLoadingUtilityConfig = false;
  isSavingUtilityConfig = false;
  isSellEnabled = false;
  isBuyEnabled = false;

  constructor(
    private utilityService: UtilityService,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.utilityId = this.route.snapshot.paramMap.get('id') ?? '';
    this.loadConfiguration();
  }

  loadConfiguration(silent = false): void {
    if (!silent) this.isLoadingUtilityConfig = true;
    this.utilityService.getUtilityConfiguration(this.utilityId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (!silent) this.isLoadingUtilityConfig = false;
          this.utilityConfig = data;
          const cfg = data.config;
          this.isSellEnabled = toBoolean(cfg?.allow_utility_to_sell);
          this.isBuyEnabled = toBoolean(cfg?.allow_utility_to_buy);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('[UtilityConfig] Load failed:', err);
          if (!silent) this.isLoadingUtilityConfig = false;
          this.cdr.markForCheck();
        },
      });
  }

  saveConfiguration(): void {
    if (!this.utilityConfig) return;

    const existingConfig: Record<string, any> = this.utilityConfig?.config || {};
    const allowedKeys = [
      'enable_monthly_bid', 'enable_day_ahead_bid', 'enable_preferential_bid',
      'allow_prosumers_to_sell', 'allow_prosumers_to_buy',
      'allow_consumers_to_sell', 'allow_consumers_to_buy',
      'allow_utility_to_sell', 'allow_utility_to_buy',
      'enable_fix_price', 'fix_price', 'min_price', 'max_price',
      'maximum_range_in_days', 'day_ahead_in_days', 'hours_left_to_close',
    ];
    const safeExisting: any = {};
    allowedKeys.forEach(key => {
      if (existingConfig[key] !== undefined) safeExisting[key] = existingConfig[key];
    });

    const updatedConfig = stringifyBooleans({
      ...safeExisting,
      allow_utility_to_sell: this.isSellEnabled,
      allow_utility_to_buy: this.isBuyEnabled,
    });

    const payload = {
      config: updatedConfig,
      validity_start: formatToApiDate(),
      validity_end: 'infinity',
    };

    this.isSavingUtilityConfig = true;
    this.utilityService.updateUtilityConfig(this.utilityId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSavingUtilityConfig = false;
          this.toastService.show('Settings updated!', 'success');
          this.loadConfiguration(true);
        },
        error: (err) => {
          this.isSavingUtilityConfig = false;
          console.error('[UtilityConfig] Save failed:', err);
          this.toastService.show('Failed to update settings.', 'error');
          this.cdr.markForCheck();
        },
      });
  }
}
